import { useMemo } from "react"

import {
  selectBorrowingFeeRate,
  selectMinimumCollateralRatioInCurrentMode,
  selectMinimumNetDebt,
  useDappSelector,
} from "#/redux-state"
import { bigIntToFloat, bigIntToHumanReadableFormat } from "#/shared/utils"

export const useMinimumCollateralRatio = () => {
  const minimumCollateralRatio = useDappSelector(
    selectMinimumCollateralRatioInCurrentMode,
  )

  const minimumCollateralRatioLabel = bigIntToFloat(
    (minimumCollateralRatio ?? 0n) * 100n,
  )

  return { minimumCollateralRatio, minimumCollateralRatioLabel }
}

export const useMinimumNetDebt = () => {
  const minimumNetDebt = useDappSelector(selectMinimumNetDebt)

  const minimumNetDebtLabel = useMemo(
    () => bigIntToHumanReadableFormat(minimumNetDebt ?? 0n),
    [minimumNetDebt],
  )

  return { minimumNetDebt, minimumNetDebtLabel }
}

export const useBorrowingFee = () => {
  const borrowingFeeRate = useDappSelector(selectBorrowingFeeRate)

  const calculateBorrowingFee = (amount: bigint): bigint => {
    if (!borrowingFeeRate || !amount || amount < 0n) return 0n

    // Fee is a percentage represented in big int format meaning that f.e.
    // 0.1% will be 10n ** 17n. We need to calculate percentage value so we
    // are performing a reverse division, f.e:
    // 1000 * 0.2 === 1000 / 5, as 0.2 === 1/5
    return amount / (10n ** 18n / borrowingFeeRate)
  }

  const borrowingFeeRateLabel = useMemo(
    () => bigIntToHumanReadableFormat((borrowingFeeRate ?? 0n) * 100n),
    [borrowingFeeRate],
  )

  return { calculateBorrowingFee, borrowingFeeRateLabel }
}
